import { useState, useEffect } from "react";
import { editarFactura } from "../../../services/compra.services";
import { parseOracleErrorMessage } from "../../../common/utils/functions/parseOracleErrorMessage";
import { Modal, Input, PrimaryButton, TextButton } from "@artprov/design-system";
import PropTypes from "prop-types";
import { useFacturaStore } from "../../../common/zustand/factura.store.js";
import { useComprasAuth } from "../../../common/hooks/useComprasAuth";

export default function EditInvoiceModal({ isOpen, onClose, initialData = {}, idComprobante, onSuccess = () => { }, onError = () => { } }) {
  const safeInitialData = initialData || {};
  const [purchaseOrder, setPurchaseOrder] = useState(initialData.ORDEN_COMPRA || "");
  const [dispatchNumber, setDispatchNumber] = useState(() => initialData.NUMERO_DESPACHO || "");
  const { username } = useComprasAuth();

  useEffect(() => {
    setPurchaseOrder(initialData.ORDEN_COMPRA || "");
    setDispatchNumber(initialData?.NUMERO_DESPACHO || "");
  }, [initialData]);

  const safeClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const validatePurchaseOrder = (value) => {
    if (!value || typeof value !== 'string') return false;
    return value.trim().length >= 1;
  };

  const getPurchaseOrderHints = (value) => {
    if (!value || value.trim().length < 1) {
      return [{
        variant: 'info',
        text: 'Debe ingresar al menos 1 caracter'
      }];
    }
    return [];
  };

  const getDispatchNumberHints = (value) => {
    if (!value) {
      return [{
        variant: 'info',
        text: 'Campo opcional'
      }];
    }
    return [];
  };

  const isFormValidWithWords = () => {
    return validatePurchaseOrder(purchaseOrder);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirmChanges = async () => {
    if (!isFormValidWithWords() || isSubmitting) return;
    setIsSubmitting(true);    
    try {
      const payload = {
        idcomp: idComprobante,
        username: username,
        orden_compra: purchaseOrder,
        num_despacho: dispatchNumber,
      };

      const response = await editarFactura(payload);
      try {
        const prev = useFacturaStore.getState().facturaActual || {};
        useFacturaStore.getState().setFacturaActual({
          ...prev,
          ORDEN_COMPRA: purchaseOrder ?? prev?.ORDEN_COMPRA,
          NUMERO_DESPACHO: dispatchNumber ?? prev?.NUMERO_DESPACHO,
        });
      } catch (error) {
        console.warn(error);
      }
      // Notificar éxito al componente padre
      try {
        if (typeof onSuccess === 'function') {
          onSuccess({ purchaseOrder, dispatchNumber, response });
        }
      } catch (cbErr) {
        console.warn(cbErr);
      }
      // Cerrar modal al finalizar correctamente
      safeClose();
    } catch (error) {
      // Si es un error 400 desde el backend, extraer mensaje legible y notificar al padre
      const status = error?.response?.status;
      if (status === 400) {
        const message = parseOracleErrorMessage(error);
        try {
          if (typeof onError === 'function') {
            onError(message);
          }
        } catch (cbErr) {
          console.warn("onError callback lanzó un error:", cbErr);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setPurchaseOrder(safeInitialData.ORDEN_COMPRA || "");
    setDispatchNumber(safeInitialData.NUMERO_DESPACHO || "");
    safeClose();
  };

  const handlePurchaseOrderChange = (event) => {
    const value = typeof event === 'string' ? event : (event?.target?.value ?? '');
    setPurchaseOrder(value);
  };

  const handleDispatchNumberChange = (event) => {
    setDispatchNumber(event);
  };

  const handleDispatchKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) return;
    const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
    if (allowed.includes(e.key)) return;
    if (/^[0-9]$/.test(e.key)) return;
    e.preventDefault();
  };

  return (
    <>
      {isOpen && (
        <div className="relative z-[999]">
          <Modal.Container
            open={isOpen}
            setOpen={safeClose}
            closeOnClickOutside={false}
            onClose={safeClose}
          >
            <Modal.Content height="360px" width="500px" className="overflow-visible">
              <Modal.Header>
                <Modal.Title className="pl-2">Editar factura</Modal.Title>
                <Modal.Close onClose={safeClose} />
              </Modal.Header>

              <Modal.Body>
                <div className="modal-content flex flex-col w-[436px]">
                  <Input
                    label="Orden de compra"
                    name="purchaseOrder"
                    id="purchaseOrder"
                    type="text"
                    value={purchaseOrder}
                    placeholder="Ingrese la orden de compra"
                    onChange={handlePurchaseOrderChange}
                    classNameContainerInput="w-full"
                    hints={getPurchaseOrderHints(purchaseOrder)}
                  />

                  <Input
                    label="Número de despacho"
                    name="dispatchNumber"
                    id="dispatchNumber"
                    type="text"
                    value={dispatchNumber}
                    placeholder="Ingrese el número de despacho"
                    onChange={handleDispatchNumberChange}
                    onKeyDown={handleDispatchKeyDown}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    classNameContainerInput="w-full"
                    hints={getDispatchNumberHints(dispatchNumber)}
                  />
                </div>
              </Modal.Body>

              <Modal.Footer className="flex justify-end">
                <div className="flex space-x-2">
                  <TextButton
                    size="small"
                    onClick={handleCancel}
                  >
                    Cancelar
                  </TextButton>
                  <PrimaryButton
                    size="small"
                    onClick={handleConfirmChanges}
                    classNameIcon="text-tertiary-100"
                    disabled={!isFormValidWithWords() || isSubmitting}
                  >
                    Confirmar cambios
                  </PrimaryButton>
                </div>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Container>
        </div>
      )}
    </>
  );
}

EditInvoiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  idComprobante: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};